import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Input from "@mui/material/Input";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import Checkbox from "@mui/material/Checkbox";
import ListSubheader from "@mui/material/ListSubheader";
import InputLabel from "@mui/material/InputLabel";
import { SFArrowDownIcon, SFSearchIcon } from "../icons";
import { getSelectOptions } from "../../services/selectService";
import LoadMoreBtn from "./loadMoreBtn";
import { Logger } from "@base-provider";
import { useTranslation } from "react-i18next";
import getStyled from "../../hooks/getStyled";
import { SFStack } from "../grid";
import SFTypography from "../typography";
import InputErrorLabel from "../inputErrorLabel";

const { styled, theme } = getStyled();

const StyledPlaceholder = styled("span", { shouldForwardProp: false })(
  ({ theme, required }) => ({
    ...(required && {
      "&:after": {
        content: "'*'",
        color: theme.palette.red400.main,
        marginLeft: theme.spacing(0.5),
      },
    }),
  })
);

const StyledInput = styled(Input)(({ theme }) => ({
  "&:before": {
    border: "none !important",
  },
  "&.Mui-focused": {
    "&:after": {
      borderBottom: `2px solid ${theme.palette.blue500.main}`,
    },
  },
}));

const StyledInputToolbar = styled(Input)(({ theme }) => ({
  ...theme.typography.pageActionBar,
  "&.MuiInputBase-root": {
    border: "none !important",
    "& .MuiInput-input": {
      paddingBottom: 0,
    },
    "&:before": {
      border: "none !important",
    },
    "&.Mui-focused": {
      borderBottom: "none !important",
      "&:after": {
        borderBottom: "none !important",
      },
    },
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  "&.MuiMenuItem-root": {
    "&:hover": {
      backgroundColor: theme.palette.blue50.main,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.blue100_1.main,
    },
  },
}));

const StyledListSubHeader = styled(ListSubheader)(({ theme }) => ({
  "&.MuiMenuItem-root": {
    "&:hover": {
      backgroundColor: theme.palette.blue50.main,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.blue100_1.main,
    },
  },
}));

const StyledOptionMenuItem = styled(MenuItem)(() => ({
  boxShadow: "0px -2px 3px 0px rgba(183, 183, 183, 0.20)",
}));

const SFSelect = (props) => {
  const [selectState, setSelectState] = useState({
    selectedValue: props.selectedValue ?? (props.multiple ? [] : ""),
    values: props.values ?? [],
    searchText: "",
    isOpen: false,
  });

  useEffect(() => {
    setSelectState((prevState) => ({
      ...prevState,
      selectedValue: props.selectedValue ?? (props.multiple ? [] : ""),
      values: props.values ?? [],
    }));
  }, [props.selectedValue]);

  const { t } = useTranslation();
  let disabledValues = props.disabledValues || [];

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (
        (selectState.searchText.trim(" ").length > 2 ||
          selectState.searchText.trim(" ").length === 0) &&
        selectState.isOpen
      ) {
        filterValuesHandler(true);
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [selectState.searchText]);

  const { selectedValue, values, searchText, isOpen } = selectState;

  const filterValuesHandler = (shouldOpen = false, valuesCount = 50) => {
    let filterValues;
    if (props.filterValuesUrl) {
      getSelectOptions(
        props.filterValuesUrl,
        JSON.stringify({
          colName: props?.name ?? "",
          filterModel: {},
        })
      )
        .then((resp) => resp.data)
        .then((values) => {
          if (searchText.trim(" ").length > 0) {
            filterValues = values.filter(
              (option) =>
                option.toLowerCase().indexOf(searchText.toLowerCase()) > -1
            );
          } else {
            filterValues = values;
          }
          filterValues = filterValues.slice(0, valuesCount);
          setSelectState((prevState) => ({
            ...prevState,
            values: filterValues,
            isOpen: shouldOpen,
          }));
        });
    } else {
      if (searchText.trim(" ").length > 0) {
        filterValues = values.filter(
          (option) =>
            option.toLowerCase().indexOf(searchText.toLowerCase()) > -1
        );
      } else {
        filterValues = props.values;
      }

      filterValues = filterValues.slice(0, valuesCount);
      setSelectState((prevState) => ({
        ...prevState,
        values: filterValues,
        isOpen: shouldOpen,
      }));
    }
  };

  const changeHandler = (event) => {
    Logger.of("App.select.onChange").info({ event: event });
    let value = event.target.value;
    if (typeof value === "object" && !value[0]) {
      value = [];
    }
    setSelectState((prevState) => ({
      ...prevState,
      selectedValue:
        typeof value === "string" && props.multiple ? value.split(",") : value,
    }));
    const appliedFilter = { name: props?.name ?? "", value: value };
    props.onChangeFilter(appliedFilter);
  };

  const openHandler = () => {
    filterValuesHandler(true);
  };

  const closeHandler = () => {
    setSelectState((prevState) => ({
      ...prevState,
      searchText: "",
      isOpen: false,
    }));
  };

  const getGroupedOptions = (values) => {
    let flattenVals = [];
    values.forEach((val) => {
      flattenVals = [
        ...flattenVals,
        ...[{ name: val.group, isGroup: true, disabled: false }],
        ...val.children.map((child) => ({
          name: child.name,
          isGroup: false,
          disabled: child.disabled,
        })),
      ];
    });
    return flattenVals.map((val, index) => {
      if (val.isGroup) {
        return (
          <StyledListSubHeader
            variant="select"
            sx={(theme) => ({ color: theme.palette.grey500.main })}
            key={index}
          >
            {val.name}
          </StyledListSubHeader>
        );
      }
      return (
        <StyledMenuItem
          className={!props.optionsWithCheckbox ? "child" : ""}
          variant="select"
          key={index}
          disabled={val.disabled}
          value={val.name}
          inGroup={!props.optionsWithCheckBox}
        >
          {props.optionsWithCheckbox && (
            <Checkbox checked={selectedValue.indexOf(val.name) > -1} />
          )}
          <ListItemText>{val.name}</ListItemText>
        </StyledMenuItem>
      );
    });
  };

  return (
    <FormControl
      hiddenLabel={!props.label}
      {...(props.variant !== "transparent" &&
        props.variant !== "toolbar" && {
        variant:
            props.variant === "filled-secondary" ? "filled" : props.variant,
      })}
      sx={(theme) => ({
        "& .MuiFilledInput-root": {
          ...(props.variant === "filled-secondary" && {
            backgroundColor: theme.palette.white.main,
            border: `1px solid ${theme.palette.grey225.main}`,
            borderBottom: "none",
          }),
        },
      })}
      error={props.hasError}
      fullWidth
      size={props.size}
    >
      {props.label && (
        <InputLabel
          shrink={
            (typeof selectedValue !== "undefined" &&
              selectedValue.length > 0) ||
            typeof props.placeholder !== "undefined"
          }
          required={props.required}
        >
          {props.label}
        </InputLabel>
      )}
      <Select
        MenuProps={{
          autoFocus: !props.hasSearch,
          style: {
            maxHeight: 400,
            borderRadius: 0,
          },
          container: props.anchorEl ?? document.body,
          disablePortal: props.disablePortal,
        }}
        displayEmpty
        required={props.required}
        {...(props.name && { id: props.name })}
        open={isOpen}
        disabled={props.disabled}
        multiple={props.multiple}
        value={
          //if valueFormatter is passed, use it to format the selected value
          props.valueFormatter
            ? props.valueFormatter(selectedValue)
            : selectedValue
        }
        onChange={changeHandler}
        onOpen={openHandler}
        className={props.className}
        {...(props.variant === "transparent" && { input: <StyledInput /> })}
        {...(props.variant === "toolbar" && {
          IconComponent: SFArrowDownIcon,
          input: <StyledInputToolbar inputProps={{ tabIndex: null }} />,
        })}
        onClose={closeHandler}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <>
                {props.placeholder ? (
                  <StyledPlaceholder required={props.required}>
                    {props.placeholder}
                  </StyledPlaceholder>
                ) : null}
              </>
            );
          }
          if (props.multiple) {
            return selected.join(", ");
          }
          return selected;
        }}
        {...(props.variant !== "toolbar" && {
          sx: { ...theme.typography.formDropdownText },
        })}
      >
        {values && props.hasSearch && (
          <ListSubheader>
            <TextField
              sx={{ mt: 2 }}
              size="small"
              // Autofocus on textfield
              autoFocus
              placeholder={t("components.select.searchPlaceholder")}
              variant="standard"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SFSearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => {
                setSelectState((prevState) => ({
                  ...prevState,
                  searchText: e.target.value,
                }));
              }}
              onKeyDown={(e) => {
                if (e.key !== "Escape") {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
        )}
        {values && values.length === 0 && (
          <MenuItem disabled variant="select">
            <ListItemText
              primaryTypographyProps={theme.typography.formDropdownText}
              primary={t("components.select.resultsPlaceholder")}
            />
          </MenuItem>
        )}
        {values && values.length > 0 && props.placeholderAsOption && (
          <MenuItem variant="placeholder" value="">
            {props.placeholder}
          </MenuItem>
        )}
        {values &&
          typeof values[0] !== "object" &&
          values.map((val) => (
            <StyledMenuItem
              key={val}
              value={val}
              variant="select"
              disabled={
                disabledValues.length > 0 && disabledValues.includes(val)
              }
            >
              {props.optionsWithCheckbox && (
                <Checkbox checked={selectedValue.indexOf(val) > -1} />
              )}
              <ListItemText
                primaryTypographyProps={theme.typography.formDropdownText}
                primary={
                  //if optionFormatter is passed, use it to format each option
                  props.optionFormatter ? props.optionFormatter(val) : val
                }
              />
            </StyledMenuItem>
          ))}
        {values && typeof values[0] === "object" && getGroupedOptions(values)}
        {values && values.length > 0 && values.length % 50 === 0 && (
          <ListSubheader variant="select">
            <div className="d-flex justify-content-center">
              <LoadMoreBtn
                limit={values.length}
                onLoadMore={(valuesCount) => {
                  Logger.of("App.select.onLoadMore").info({
                    valuesCount: valuesCount,
                  });
                  filterValuesHandler(true, valuesCount);
                }}
              />
            </div>
          </ListSubheader>
        )}
        {props.optionButton && (
          <StyledOptionMenuItem
            key={props.optionButton.value}
            value={props.optionButton.value}
            variant="select"
          >
            <SFStack direction="row" alignItems="center" spacing={0.5}>
              {props.optionButton.icon}
              <SFTypography variant="buttonDefault" color="blue500">
                {props.optionButton.value}
              </SFTypography>
            </SFStack>
          </StyledOptionMenuItem>
        )}
      </Select>
      {props.hasError && (
        <FormHelperText component="div">
          <InputErrorLabel label={props.helperText} />
        </FormHelperText>
      )}
    </FormControl>
  );
};

SFSelect.defaultProps = {
  optionsWithCheckbox: false,
  multiple: false,
  size: "small",
  hasSearch: false,
  required: false,
  hasError: false,
  helperText: "This is required",
  variant: "filled",
};

SFSelect.propTypes = {
  name: PropTypes.string,
  onChangeFilter: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  values: PropTypes.array,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  optionsWithCheckbox: PropTypes.bool,
  multiple: PropTypes.bool,
  filterValuesUrl: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  variant: PropTypes.oneOf([
    "filled",
    "filled-secondary",
    "outlined",
    "standard",
    "transparent",
    "toolbar",
  ]),
  hasSearch: PropTypes.bool,
  required: PropTypes.bool,
  hasError: PropTypes.bool,
  helperText: PropTypes.string,
  placeholderAsOption: PropTypes.bool,
  disabled: PropTypes.bool,
  optionFormatter: PropTypes.func,
  valueFormatter: PropTypes.func,
  optionButton: PropTypes.shape({
    value: PropTypes.string,
    icon: PropTypes.element,
  }),
};

export default SFSelect;
